import React, { useEffect, useState } from 'react';
import { Typography, Table, Tag, Button, Space, Modal, Form, Input, Select, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';

// Coaching Common Links: links used again and again on sessions. A link
// applies to its campaigns, or to every campaign when it has none.
//   CommonLinksConfig  the table on Coaching Config (add / edit / delete)
//   CommonLinksPicker  the session page's "Select Common Links" popup

const campaignTags = (camps) => ((camps || []).length
  ? <Space size={[0, 4]} wrap>{camps.map((c) => <Tag key={c.ID}>{c.Name}</Tag>)}</Space>
  : <Tag color="blue">All campaigns</Tag>);

export default function CommonLinksConfig({ isAdmin }) {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [modal, setModal]         = useState(null);   // null | { link }
  const [saving, setSaving]       = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/coaching/common-links');
      setRows(res.data || []);
    } catch { message.error('Failed to load common links.'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get('/api/admin/campaigns').then((r) => setCampaigns(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = { url: v.url, label: v.label || null, campaign_ids: v.campaign_ids || [] };
      if (modal.link) await api.put(`/api/coaching/common-links/${modal.link.ID}`, body);
      else            await api.post('/api/coaching/common-links', body);
      setModal(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (link) => {
    try {
      await api.delete(`/api/coaching/common-links/${link.ID}`);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  const selected = modal?.link?.Campaigns?.map((c) => c.ID) || [];

  // Fill the form for the link clicked each time the popup opens: the form
  // instance outlives the popup, so starting values alone could show the
  // link edited before.
  useEffect(() => {
    if (!modal) return;
    form.resetFields();
    form.setFieldsValue({
      url: modal.link?.URL || '',
      label: modal.link?.Label || '',
      campaign_ids: modal.link?.Campaigns?.map((c) => c.ID) || [],
    });
  }, [modal, form]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>Common Links</Typography.Title>
        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ link: null })}>Add</Button>}
      </div>
      <Table
        rowKey="ID"
        dataSource={rows}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No common links yet.' }}
        columns={[
          { title: 'Label', dataIndex: 'Label', key: 'Label', width: 240, render: (v) => v || '—' },
          { title: 'URL', dataIndex: 'URL', key: 'URL', ellipsis: true,
            render: (v) => <a href={v} target="_blank" rel="noopener noreferrer">{v}</a> },
          { title: 'Campaigns', dataIndex: 'Campaigns', key: 'Campaigns', width: 320, render: campaignTags },
          ...(isAdmin ? [{
            title: '', key: 'actions', width: 130, align: 'right',
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => setModal({ link: r })}>Edit</Button>
                <Popconfirm title="Delete this link?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(r)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          }] : []),
        ]}
      />

      <Modal maskClosable={false}
        title={modal?.link ? 'Edit Common Link' : 'Add Common Link'}
        open={!!modal}
        onOk={save}
        onCancel={() => setModal(null)}
        confirmLoading={saving}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
          <Form.Item label="URL" name="url"
            rules={[{ required: true, message: 'URL is required' }, { type: 'url', message: 'Enter a valid URL' }]}>
            <Input placeholder="https://" />
          </Form.Item>
          <Form.Item label="Label" name="label" rules={[{ max: 255 }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Campaigns" name="campaign_ids">
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="All campaigns"
              options={campaigns.filter((c) => c.IsActive || selected.includes(c.ID))
                .map((c) => ({ value: c.ID, label: c.Name }))} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// The links for a session's campaign (and the all-campaign ones); ticking
// some and confirming hands them to onPick. Ones already on the session are
// shown but cannot be picked again.
export function CommonLinksPicker({ open, campaignId, existingUrls = [], onClose, onPick }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked]   = useState([]);

  useEffect(() => {
    if (!open) return;
    setPicked([]);
    setLoading(true);
    api.get('/api/coaching/common-links', { params: { campaign_id: campaignId ?? 0 } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, campaignId]);

  const already = new Set(existingUrls.map((u) => (u || '').trim()));

  return (
    <Modal maskClosable={false}
      title="Select Common Links"
      open={open}
      onCancel={onClose}
      onOk={() => onPick(rows.filter((r) => picked.includes(r.ID)))}
      okText="Add Selected"
      okButtonProps={{ disabled: !picked.length }}
      width={760}
      destroyOnClose
    >
      <Table
        rowKey="ID"
        dataSource={rows}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No common links for this campaign.' }}
        rowSelection={{
          selectedRowKeys: picked,
          onChange: setPicked,
          getCheckboxProps: (r) => ({ disabled: already.has(r.URL.trim()) }),
        }}
        columns={[
          { title: 'Label', dataIndex: 'Label', key: 'Label', width: 220,
            render: (v, r) => (already.has(r.URL.trim())
              ? <span>{v || '—'} <Tag style={{ marginLeft: 4 }}>Added</Tag></span>
              : (v || '—')) },
          { title: 'URL', dataIndex: 'URL', key: 'URL', ellipsis: true,
            render: (v) => <a href={v} target="_blank" rel="noopener noreferrer">{v}</a> },
          { title: 'Campaigns', dataIndex: 'Campaigns', key: 'Campaigns', width: 200, render: campaignTags },
        ]}
      />
    </Modal>
  );
}
